// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, finalize, from, map, Observable, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { ApiService } from '../../api.service';

export type FileMetaSource = 'head' | 'range' | 'blob' | 'attributes';

export type FileMeta = {
  filename: string;
  size: number | null;
  source: FileMetaSource;
  expiresAt?: number;
};

@Injectable({ providedIn: 'root'})
export class HceApiService {
  public readonly post: ApiService['post'];
  public readonly get: ApiService['get'];
  public readonly put: ApiService['put'];
  public readonly patch: ApiService['patch'];
  public readonly delete: ApiService['delete'];
  
  private readonly rawHttp: HttpClient;
  private readonly emptyHeaders = new HttpHeaders();
  private readonly range0Headers = new HttpHeaders({ Range: 'bytes=0-0'});
  private readonly metaCache = new Map<string, FileMeta>();
  private readonly inFlight = new Map<string, Observable<FileMeta>>();
  private readonly defaultTtlMs = 60 * 1000;

  constructor(
    private readonly http: HttpClient,
    private readonly api: ApiService,
    httpBackend: HttpBackend,
  ) {
    this.post = (...args) => this.api.post(...args);
    this.get = (...args) => this.api.get(...args);
    this.put = (...args) => this.api.put(...args);
    this.patch = (...args) => this.api.patch(...args);
    this.delete = (...args) => this.api.delete(...args);

    this.rawHttp = new HttpClient(httpBackend);
  }

  public getArrayBuffer(url: string): Observable<ArrayBuffer> {
    return this.fetchResponse(url).pipe(
      switchMap(res => res.arrayBuffer())
    );
  }

  public getRawText(url: string): Observable<string> {
    return this.fetchResponse(url).pipe(
      switchMap(res => res.text())
    );
  }

  public getBlob(url: string): Observable<Blob> {
    return this.fetchResponse(url).pipe(
      switchMap(res => res.blob())
    );
  }

  public getFileMeta(
    url: string,
    options?: {
      forceRefresh?: boolean;
      ttlMs?: number;
      failFastStatuses?: number[];
    }
  ): Observable<FileMeta> {
    const now = Date.now();
    const ttlMs = options?.ttlMs ?? this.defaultTtlMs;
    const cached = this.metaCache.get(url);
    if (!options?.forceRefresh && cached) {
      if (cached.expiresAt && cached.expiresAt > now) {
        return of(cached);
      }
      this.metaCache.delete(url);
    }
    const inflight = this.inFlight.get(url);
    if (inflight) {
      return inflight;
    }
    const obs = this.tryHead(url, options).pipe(
      switchMap(meta => {
        if (meta) {
          return of(meta);
        }
        return this.tryRange(url);
      }),
      switchMap(meta => {
        if (meta) {
          return of(meta);
        }
        return this.fallbackBlob(url);
      }),
      map(meta => {
        const signedExpiry = this.estimateExpiryFromSignedUrl(url);
        const expiresAt = signedExpiry ?? (Date.now() + ttlMs);
        return {
          ...meta,
          expiresAt
        };
      }),
      tap(meta => this.metaCache.set(url, meta)),
      catchError(this._handleError),
      shareReplay({ bufferSize: 1, refCount: false }),
      finalize(() => this.inFlight.delete(url))
    );
    this.inFlight.set(url, obs);
    return obs;
  }

  public getFileName(url: string): Observable<string> {
    return this.getFileMeta(url).pipe(map(m => m.filename));
  }

  public getFileSize(url: string): Observable<number | null> {
    return this.getFileMeta(url).pipe(map(m => m.size));
  }

  private tryHead(
    url: string,
    options?: {
      forceRefresh?: boolean;
      ttlMs?: number;
      failFastStatuses?: number[];
    }
  ): Observable<FileMeta | null> {
    return this.rawHttp.head<null>(url, {
      headers: this.emptyHeaders,
      observe: 'response'
    }).pipe(
      map(response => {
        const filename = this.getDownloadFilename(response.headers, url);
        const cl = response.headers.get('Content-Length') ?? response.headers.get('content-length');
        if (cl && Number.isFinite(parseInt(cl, 10))) {
          return {
            filename,
            size: parseInt(cl, 10),
            source: 'head' as const
          };
        }
        return null;
      }),
      catchError((e: HttpErrorResponse) => {
        const failFast = options?.failFastStatuses ?? [401, 403, 404, 410];
        if (failFast.includes(e.status)) {
          return throwError(() => e);
        }
        return of(null);
      })
    );
  }

  private tryRange(url: string): Observable<FileMeta | null> {
    return this.http.get(url, {
      headers: this.range0Headers,
      observe: 'response',
      responseType: 'blob',
    }).pipe(
      map(response => {
        const filename = this.getDownloadFilename(response.headers, url);
        if (response.status === 206) {
          const cr = response.headers.get('Content-Range');
          const m = cr && /bytes\s+\d+-\d+\/(\d+)/i.exec(cr);
          if (m) {
            return {
              filename,
              size: parseInt(m[1], 10),
              source: 'range' as const,
            };
          }
        }
        if (response.status === 200 && response.body instanceof Blob) {
          return {
            filename,
            size: response.body.size,
            source: 'blob' as const,
          };
        }
        return null;
      }),
      catchError(() => of(null))
    );
  }

  private fallbackBlob(url: string): Observable<FileMeta> {
    return this.http.get(url, {
      headers: this.emptyHeaders,
      observe: 'response',
      responseType: 'blob',
    }).pipe(
      map(response => {
        const blob = response.body as Blob;
        const filename = this.getDownloadFilename(response.headers, url);
        const size = blob?.size ?? null;
        return {
          filename,
          size,
          source: 'blob'
        };
      })
    )
  }

  private estimateExpiryFromSignedUrl(url: string): number | undefined {
    try {
      const u = new URL(url);
      const expiresSec = u.searchParams.get('X-Goog-Expires');
      const startStr = u.searchParams.get('X-Goog-Date');
      if (expiresSec && startStr) {
        const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(startStr);
        if (m) {
          const start = Date.UTC(
            parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10),
            parseInt(m[4], 10), parseInt(m[5], 10), parseInt(m[6], 10),
          );
          return start + parseInt(expiresSec, 10) * 1000;
        }
      }
    } catch {
      // ignore parse errors
    }
    return undefined;
  }

  public getDownloadFilename(
    headers: HttpHeaders,
    url: string,
  ): string {
    const cd =
      headers.get('Content-Disposition') ??
      headers.get('content-disposition') ??
      headers.get('CONTENT-DISPOSITION') ??
      null;

    const fromCd = cd && this.extractFilenameFromContentDisposition(cd);
    if (fromCd) return fromCd;

    return this.filenameFromSignedUrl(url);
  }

  private extractFilenameFromContentDisposition(cd: string): string | null {
    const sanitize = (name: string): string => {
      const last = name.split(/[/\\]/).pop() ?? name;
      return last.trim();
    };

    // RFC 5987: filename*
    const mStar = /filename\*\s*=\s*([^']*)''([^;]+)/i.exec(cd);
    if (mStar) {
      try {
        return sanitize(decodeURIComponent(mStar[2]));
      } catch {
        return sanitize(mStar[2]);
      }
    }

    // filename="<...>"
    const mQuoted = /filename\s*=\s*"([^"]+)"/i.exec(cd);
    if (mQuoted) {
      return sanitize(mQuoted[1]);
    }

    // filename=<...>
    const mBare = /filename\s*=\s*([^;]+)/i.exec(cd);
    if (mBare) {
      return sanitize(mBare[1]);
    }

    return null;
  }

  private filenameFromSignedUrl(raw: string): string {
    const safeDecode = (s: string) => {
      try { return decodeURIComponent(s); } catch { return s; }
    };
    try {
      const u = new URL(raw);
      const host = u.host;
      const path = u.pathname;
      let objectPath = '';
      if (host.endsWith('.storage.googleapis.com')) {
        // https://<bucket>.storage.googleapis.com/<object>
        objectPath = path.replace(/^\/+/, '');
      } else if (host === 'storage.googleapis.com' || host.endsWith('storage.googleapis.com')) {
        // https://storage.googleapis.com/<bucket>/<object>
        const parts = path.split('/').filter(Boolean);
        objectPath = parts.slice(1).join('/');
      } else {
        // Fallback: last segment of URL path
        const last = path.split('/').filter(Boolean).pop() ?? '';
        return safeDecode(last);
      }
      const last = (objectPath.split('/').pop() ?? '').trim();
      return decodeURIComponent(last);
    } catch {
      const hashCut = raw.split('#')[0];
      const noQuery = hashCut.split('?')[0];
      const last = noQuery.substring(noQuery.lastIndexOf('/') + 1);
      return safeDecode(last);
    }
  }

  private fetchFn: typeof fetch = (...args) => fetch(...args);

  private fetchResponse(url: string): Observable<Response> {
    return from(
      this.fetchFn(url, {
        method: 'GET'
      })
    ).pipe(
      switchMap((res: Response) => {
        if (!res.ok) {
          return throwError(() => new Error(`HTTP error! status: ${res.status}`));
        }
        return [res];
      }),
      catchError(err => throwError(() => this.handleFetchError(err)))
    );
  }

  private handleFetchError(error: unknown): Error {
    if (error instanceof Error) {
      return error;
    }
    return new Error('Unknown fetch error');
  }

  private _handleError(error: HttpErrorResponse): Observable<never> {
    let reason = '';
    if (error.error) {
      if (typeof error.error === 'string') {
        reason = error.error;
      }
      else if (error.error.message) {
        reason = error.error.message;
      }
    }
    let errorMsg = reason
      ? `An error occurred: ${reason}`
      : `Server returned code: ${error.status}, error message is: ${error.message}`;
    console.error(errorMsg);
    return throwError(() => errorMsg);
  }
}
